<script lang="ts">
  import { page } from '$app/stores';
  import { goto } from '$app/navigation';
  import { t as __t, getLocale, onLocaleChange } from '$lib/i18n';

  let {
    user = null,
  }: {
    user?: any;
  } = $props();

  // Reactive locale for template reactivity
  let _lcv = $state(0);
  $effect(() => {
    const unsub = onLocaleChange(() => { _lcv++; });
    return unsub;
  });

  function _t(key: string): string {
    _lcv;
    return __t(key);
  }

  let tooltip = $state({ show: false, text: '', x: 0, y: 0 });
  let windowHeight = $state(0);
  let windowWidth = $state(0);

  // Track page URL for active route detection
  let currentRoute = $state('');
  $effect(() => {
    const unsub = page.subscribe((p) => {
      currentRoute = p.url.pathname;
    });
    return unsub;
  });

  import { FEATURE_FLAGS } from '$lib/firebase/feature-flags';

  let isHub = $derived(currentRoute === '/');
  let isVoicePlanActive = $derived(currentRoute.startsWith('/voice-plan'));
  let isPortalActive = $derived(currentRoute.startsWith('/portal'));
  let isPlaylistActive = $derived(
    currentRoute.startsWith('/playlist') || currentRoute.startsWith('/playlists'),
  );
  let isBookActive = $derived(
    currentRoute.startsWith('/digital-guide') || currentRoute.startsWith('/book'),
  );

  let isAdmin = $derived(currentRoute.startsWith('/admin'));
  let canAdminOrgs = $derived(
    user?.hasFeature?.(FEATURE_FLAGS.ORGANIZATION_ADMIN) || false
  );
  let canEmailTemplates = $derived(
    user?.hasFeature?.(FEATURE_FLAGS.EMAIL_TEMPLATES) || user?.hasFeature?.(FEATURE_FLAGS.ADMIN) || false
  );
  let canExerciseEditor = $derived(
    user?.hasFeature?.(FEATURE_FLAGS.EXERCISE_EDITOR) || false
  );

  function handlePortalClick() {
    if (hasValidSub) {
      if (user && !user.hasFeature('PORTAL_V2')) {
        window.location.href = 'http://portaal.universalvoice.eu/';
      } else {
        goto('/portal');
      }
    } else {
      goto('/checkout');
    }
  }

  function handleVoicePlanClick() {
    if (hasValidSub) {
      goto('/voice-plans');
    } else {
      goto('/checkout');
    }
  }

  function handleBookClick() {
    goto('/digital-guide');
  }

  function handlePlaylistClick() {
    if (hasValidSub) {
      goto('/playlists');
    } else {
      goto('/checkout');
    }
  }

  function showTooltip(e: MouseEvent, text: string) {
    // Only show tooltips on non-touch / larger devices to prevent "weird temp tooltips" on mobile
    const isMobile = typeof window !== 'undefined' && (window.innerWidth < 768 || window.matchMedia('(pointer: coarse)').matches);
    if (isMobile) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    tooltip = { show: true, text, x: rect.left + rect.width / 2, y: rect.top - 10 };
  }

  function hideTooltip() {
    tooltip = { show: false, text: '', x: 0, y: 0 };
  }

  // Track window height and width
  $effect(() => {
    if (typeof window === 'undefined') return;
    function onResize() {
      windowHeight = window.innerHeight;
      windowWidth = window.innerWidth;
    }
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  });

  // Hub mode positioning — dynamically computed to always fit within viewport
  // Uses percentage of viewport height so it scales on all screen sizes
  let hubStyle = $derived.by(() => {
    if (!isHub || !windowHeight) return '';

    const isMobile = windowWidth > 0 && windowWidth < 768;

    // Position bar below header (estimated), scaling with viewport
    const relativeTop = isMobile ? 0.25 : 0.30;
    const topPos = Math.floor(windowHeight * relativeTop);

    // Ensure bar fits within viewport with bottom margin
    const bottomMargin = 20;
    const maxHeight = Math.max(300, windowHeight - topPos - bottomMargin);

    return `top: ${topPos}px; max-height: ${maxHeight}px; overflow: hidden;`;
  });

  const subscriptionRequiredTooltip = $derived(_t('menu.subscriptionRequired.tooltip'));
  const hasValidSub = $derived(user?.hasValidSubscription?.());
</script>

<svelte:window bind:innerHeight={windowHeight} />

{#if isAdmin}
  <!-- Admin Floating Tab Bar -->
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <nav class="bottom-tab-bar">
    <button
      class="tab-item accent-portal"
      class:active={currentRoute === '/admin' || currentRoute.startsWith('/admin/users') || currentRoute.startsWith('/admin/user')}
      onclick={() => goto('/admin/users')}
    >
      <div class="tab-content">
        <i class="fas fa-users"></i>
        <h3>Users</h3>
      </div>
    </button>

    {#if canAdminOrgs}
      <button
        class="tab-item accent-voice"
        class:active={currentRoute.startsWith('/admin/organizations')}
        onclick={() => goto('/admin/organizations')}
      >
        <div class="tab-content">
          <i class="fas fa-building"></i>
          <h3>Organizations</h3>
        </div>
      </button>
    {/if}

    {#if canEmailTemplates}
      <button
        class="tab-item accent-playlist"
        class:active={currentRoute.startsWith('/admin/email-templates')}
        onclick={() => goto('/admin/email-templates')}
      >
        <div class="tab-content">
          <i class="fas fa-envelope"></i>
          <h3>Templates</h3>
        </div>
      </button>
    {/if}

    {#if canExerciseEditor}
      <button
        class="tab-item accent-voice"
        class:active={currentRoute.startsWith('/admin/exercise-editor')}
        onclick={() => goto('/admin/exercise-editor')}
      >
        <div class="tab-content">
          <i class="fas fa-edit"></i>
          <h3>Exercises</h3>
        </div>
      </button>
    {/if}

    <button class="tab-item accent-book" onclick={() => goto('/book-links')}>
      <div class="tab-content">
        <i class="fas fa-link"></i>
        <h3>{_t('booklinks.title')}</h3>
      </div>
    </button>

    <button
      class="tab-item accent-book"
      class:active={currentRoute.startsWith('/admin/jobs')}
      onclick={() => goto('/admin/jobs')}
    >
      <div class="tab-content">
        <i class="fas fa-bolt"></i>
        <h3>Jobs</h3>
      </div>
    </button>

    <button
      class="tab-item accent-portal"
      class:active={currentRoute.startsWith('/admin/revenue')}
      onclick={() => goto('/admin/revenue')}
    >
      <div class="tab-content">
        <i class="fas fa-chart-line"></i>
        <h3>Metrics</h3>
      </div>
    </button>
  </nav>
{:else}
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <nav
    class="bottom-tab-bar data-[hub=true]:w-[90%] data-[hub=true]:h-auto data-[hub=true]:max-h-screen data-[hub=true]:min-h-[400px] data-[hub=true]:bg-none data-[hub=true]:backdrop-filter-none data-[hub=true]:border-0 data-[hub=true]:shadow-none data-[hub=true]:flex-row data-[hub=true]:flex-nowrap data-[hub=true]:items-stretch data-[hub=true]:justify-center data-[hub=true]:gap-5 data-[hub=true]:px-5 data-[hub=true]:pointer-events-none"
    data-hub={isHub || undefined}
    style={isHub ? hubStyle : ''}
  >
    <button
      class="tab-item accent-portal"
      class:active={isPortalActive}
      class:disabled={!hasValidSub}
      onclick={handlePortalClick}
      onmouseenter={!hasValidSub ? (e) => showTooltip(e, subscriptionRequiredTooltip) : undefined}
      onmouseleave={hideTooltip}
    >
      <div class="tab-content">
        <i class="fas fa-book"></i>
        <h3>{_t('menu.portal_lower') || 'Portal'}</h3>
      </div>
      {#if isHub}
        <p class="description">{_t('menu.portal.description')}</p>
      {/if}
    </button>

    <button
      class="tab-item accent-voice"
      class:active={isVoicePlanActive}
      class:disabled={!hasValidSub}
      onclick={handleVoicePlanClick}
      onmouseenter={!hasValidSub ? (e) => showTooltip(e, subscriptionRequiredTooltip) : undefined}
      onmouseleave={hideTooltip}
    >
      <div class="tab-content">
        <i class="fas fa-microphone"></i>
        <h3>{_t('menu.voicePlans') || 'Voice Plans'}</h3>
      </div>
      {#if isHub}
        <p class="description">{_t('menu.voicePlans.description')}</p>
      {/if}
    </button>

    <button
      class="tab-item accent-playlist"
      class:active={isPlaylistActive}
      class:disabled={!hasValidSub}
      onclick={handlePlaylistClick}
      onmouseenter={!hasValidSub ? (e) => showTooltip(e, subscriptionRequiredTooltip) : undefined}
      onmouseleave={hideTooltip}
    >
      <div class="tab-content">
        <i class="fas fa-music"></i>
        <h3>{_t('menu.playlist') || 'Playlist'}</h3>
      </div>
      {#if isHub}
        <p class="description">{_t('menu.playlist.description')}</p>
      {/if}
    </button>

    <button
      class="tab-item accent-book"
      class:active={isBookActive}
      onclick={handleBookClick}
    >
      <div class="tab-content">
        <i class="fas fa-university"></i>
        <h3>{_t('menu.book') || 'Book'}</h3>
      </div>
      {#if isHub}
        <p class="description">{_t('menu.book.description')}</p>
      {/if}
    </button>
  </nav>
{/if}

{#if tooltip.show}
  <div
    class="fixed bg-black/90 text-white px-3 py-2 rounded-lg text-xs whitespace-nowrap z-[1000] -translate-x-1/2 -translate-y-full pointer-events-none shadow-lg"
    style="left: {tooltip.x}px; top: {tooltip.y}px;"
  >
    {tooltip.text}
    <div class="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-5 border-r-5 border-t-5 border-transparent border-t-black/90"></div>
  </div>
{/if}

<style>
  .bottom-tab-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    border-top: 1px solid #333;
    z-index: 30;
    min-height: 70px;
    max-height: 70px;
    padding: 0 env(safe-area-inset-right) calc(env(safe-area-inset-bottom, 0px) + 80px) env(safe-area-inset-left);
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
    transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
    background: rgba(20, 20, 20);
  }

  .tab-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(20, 20, 20, 0.9);
    border: none;
    color: #999;
    font-size: 0.8rem;
    padding: 35px 4px 8px 4px;
    cursor: pointer;
    transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  .tab-item:hover, .tab-item.active { color: #f7941d; }

  .tab-item.accent-portal:hover, .tab-item.accent-portal.active { color: #3e8ed0; }
  .tab-item.accent-voice:hover, .tab-item.accent-voice.active { color: #f7941d; }
  .tab-item.accent-playlist:hover, .tab-item.accent-playlist.active { color: #48c774; }
  .tab-item.accent-book:hover, .tab-item.accent-book.active { color: #9b51e0; }

  .tab-item.disabled { opacity: 0.5; }
  .tab-item.disabled:hover { color: #999; }

  .tab-item i { font-size: 1.2rem; margin-bottom: 4px; }
  .tab-item h3 { font-size: 0.7rem; font-weight: 500; text-align: center; line-height: 1.1; margin: 0; }
  .tab-item .description { display: none; }

  /* Hub mode styles */
  .bottom-tab-bar[data-hub] {
    max-height: 100vh;
    min-height: 400px;
    background: none;
    backdrop-filter: none;
    border: 0;
    box-shadow: none;
    pointer-events: none;
    left: 50%;
    transform: translateX(-50%);
    bottom: auto;
    overflow: hidden;
  }

  .bottom-tab-bar[data-hub] .tab-item {
    pointer-events: auto;
    flex: 1;
    min-width: 0;
    min-height: 0;
    width: auto;
    aspect-ratio: auto;
    font-size: 1rem;
    background: rgba(20, 20, 20, 0.9);
    border: 1px solid #333;
    margin: 0;
    border-radius: 20px;
    padding: 20px;
    justify-content: center;
    align-items: center;
    text-align: center;
    backdrop-filter: blur(10px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }

  .bottom-tab-bar[data-hub] .tab-item:first-child,
  .bottom-tab-bar[data-hub] .tab-item:last-child {
    border-radius: 20px;
    padding: 20px;
  }

  .bottom-tab-bar[data-hub] .tab-item:hover {
    transform: none !important;
    box-shadow: none;
    background: rgba(30, 30, 30, 0.95);
    z-index: 1;
  }

  .bottom-tab-bar[data-hub] .tab-item i { font-size: 3rem; margin-bottom: 20px; }
  .bottom-tab-bar[data-hub] .tab-item h3 { font-size: 1.2rem; font-weight: 700; margin-bottom: 10px; color: #fff; }

  .bottom-tab-bar[data-hub] .tab-item .description {
    display: block;
    font-size: 0.85rem;
    color: #aaa;
    line-height: 1.4;
    margin-bottom: auto;
    max-width: 90%;
  }

  /* Hub mode accent colors */
  .bottom-tab-bar[data-hub] .tab-item.accent-portal:hover,
  .bottom-tab-bar[data-hub] .tab-item.accent-portal.active {
    box-shadow: 0 0 25px rgba(62, 142, 208, 0.6);
    color: #3e8ed0 !important;
    border-bottom: 4px solid #3e8ed0;
  }
  .bottom-tab-bar[data-hub] .tab-item.accent-portal:hover i,
  .bottom-tab-bar[data-hub] .tab-item.accent-portal.active i { color: #3e8ed0; }

  .bottom-tab-bar[data-hub] .tab-item.accent-voice:hover,
  .bottom-tab-bar[data-hub] .tab-item.accent-voice.active {
    box-shadow: 0 0 25px rgba(247, 148, 29, 0.6);
    color: #f7941d !important;
    border-bottom: 4px solid #f7941d;
  }
  .bottom-tab-bar[data-hub] .tab-item.accent-voice:hover i,
  .bottom-tab-bar[data-hub] .tab-item.accent-voice.active i { color: #f7941d; }

  .bottom-tab-bar[data-hub] .tab-item.accent-playlist:hover,
  .bottom-tab-bar[data-hub] .tab-item.accent-playlist.active {
    box-shadow: 0 0 25px rgba(72, 199, 116, 0.6);
    color: #48c774 !important;
    border-bottom: 4px solid #48c774;
  }
  .bottom-tab-bar[data-hub] .tab-item.accent-playlist:hover i,
  .bottom-tab-bar[data-hub] .tab-item.accent-playlist.active i { color: #48c774; }

  .bottom-tab-bar[data-hub] .tab-item.accent-book:hover,
  .bottom-tab-bar[data-hub] .tab-item.accent-book.active {
    box-shadow: 0 0 25px rgba(155, 81, 224, 0.6);
    color: #9b51e0 !important;
    border-bottom: 4px solid #9b51e0;
  }
  .bottom-tab-bar[data-hub] .tab-item.accent-book:hover i,
  .bottom-tab-bar[data-hub] .tab-item.accent-book.active i { color: #9b51e0; }

  /* Desktop floating pill */
  @media (min-width: 1024px) {
    .bottom-tab-bar:not([data-hub]) {
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      right: auto;
      min-width: max-content;
      width: auto;
      max-width: calc(100vw - 40px);
      height: auto;
      min-height: 70px;
      max-height: 70px;
      flex-direction: row;
      border-top: none;
      border: none;
      border-radius: 35px;
      padding: 0;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
      backdrop-filter: none;
      background: none;
    }

    .bottom-tab-bar:not([data-hub]) .tab-item {
      flex: 1;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 12px 20px;
      width: auto;
      font-size: 1rem;
      margin: 0;
      border-radius: 0;
      position: relative;
    }

    .bottom-tab-bar:not([data-hub]) .tab-item:first-of-type {
      border-radius: 35px 0 0 35px;
      padding-left: 24px;
    }

    .bottom-tab-bar:not([data-hub]) .tab-item:last-of-type {
      border-radius: 0 35px 35px 0;
      padding-right: 24px;
    }

    .bottom-tab-bar:not([data-hub]) .tab-item:hover::before,
    .bottom-tab-bar:not([data-hub]) .tab-item.active::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-radius: inherit;
      z-index: -1;
    }

    .bottom-tab-bar:not([data-hub]) .tab-item:hover::before { background: rgba(247, 148, 29, 0.1); }
    .bottom-tab-bar:not([data-hub]) .tab-item.active::before { background: rgba(247, 148, 29, 0.2); }

    .bottom-tab-bar:not([data-hub]) .tab-item i { font-size: 1.2rem; margin-bottom: 4px; margin-right: 0; }
    .bottom-tab-bar:not([data-hub]) .tab-item h3 { font-size: 0.75rem; font-weight: 500; }

    /* Hub mode desktop */
    .bottom-tab-bar[data-hub] {
      max-width: 900px;
      display: flex;
      flex-direction: row;
      border: none;
      background: none;
      box-shadow: none;
      backdrop-filter: none;
      padding: 0;
      left: 50%;
      transform: translateX(-50%);
      bottom: auto;
    }

    .bottom-tab-bar[data-hub] .tab-item {
      flex: 1;
      min-width: 0;
      min-height: 0;
      width: auto;
      margin: 10px;
    }

    .bottom-tab-bar[data-hub] .tab-item:hover::before,
    .bottom-tab-bar[data-hub] .tab-item.active::before { display: none; }
  }

  /* Mobile adjustments */
  @media (max-width: 768px) {
    .bottom-tab-bar[data-hub] {
      width: 100%;
      padding: 0 15px;
      gap: 15px;
      flex-direction: column;
      height: auto;
      min-height: auto;
      left: 0;
      right: 0;
      transform: none;
      width: 100%;
      bottom: auto;
    }

    .bottom-tab-bar[data-hub] .tab-item {
      width: 100%;
      flex: 1;
      min-width: 0;
      min-height: 0;
      aspect-ratio: auto;
      padding: 15px;
    }

    .bottom-tab-bar[data-hub] .tab-item i { font-size: 2.2rem; margin-bottom: 15px; }
    .bottom-tab-bar[data-hub] .tab-item h3 { font-size: 1rem; }
    .bottom-tab-bar[data-hub] .tab-item .description { font-size: 0.75rem; }
  }

  @media (max-width: 1024px) {
    .tab-item { border-radius: 0; }
  }
</style>
